/**
 * DesktopScheduleView Component
 * Desktop table view for schedule management
 */

import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  TextField,
  Checkbox,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import EmailIcon from '@mui/icons-material/Email';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SearchIcon from '@mui/icons-material/Search';
import dayjs from 'dayjs';

import { DAYS } from '../constants';
import { formatShiftDisplay } from '../utils/timeHelpers';

/**
 * DesktopScheduleView - Table view for desktop
 */
export default function DesktopScheduleView({
  // Data
  selectedWeek,
  employees,
  shifts,
  positions,
  searchTerm,
  selectedPosition,
  selectedForCopy,
  generatingFixed,
  // Handlers
  onWeekChange,
  onSearchChange,
  onPositionChange,
  onSelectedForCopyChange,
  onOpenCopyWeek,
  onGenerateFixed,
  onNotify,
  onOpenEmployee,
  onCellClick,
  onDeleteShift,
  getShiftForCell,
  getEmployeeDisplayPosition,
}) {
  return (
    <>
      {/* Week Navigator */}
      <Paper sx={{
        p: { xs: 1.5, md: 2.5 },
        mb: 2,
        borderRadius: 3,
        background: 'linear-gradient(135deg, rgba(0,128,96,0.04) 0%, rgba(0,128,96,0.08) 100%)',
        border: '1px solid rgba(0,128,96,0.12)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 1, md: 2 } }}>
          <IconButton
            onClick={() => onWeekChange(selectedWeek.subtract(1, 'week').startOf('isoWeek'))}
            aria-label="Προηγούμενη εβδομάδα"
            sx={{
              bgcolor: 'white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              '&:hover': { bgcolor: 'primary.main', color: 'white' },
              transition: 'all 0.2s',
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: { xs: 2, md: 3 },
            py: 1,
            bgcolor: 'white',
            borderRadius: 12,
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            border: '1px solid rgba(0,0,0,0.06)',
          }}>
            <CalendarMonthIcon sx={{ color: 'primary.main', fontSize: 22 }} />
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {selectedWeek.format('DD/MM')} — {selectedWeek.add(6, 'day').format('DD/MM/YYYY')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Εβδομάδα {selectedWeek.isoWeek()}
              </Typography>
            </Box>
            <DatePicker
              value={selectedWeek}
              onChange={(newValue) => onWeekChange(dayjs(newValue).startOf('isoWeek'))}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { width: 0, minWidth: 0, overflow: 'hidden', '& fieldset': { border: 'none' } },
                },
                openPickerButton: { sx: { ml: -1 } },
              }}
            />
          </Box>
          <IconButton
            onClick={() => onWeekChange(selectedWeek.add(1, 'week').startOf('isoWeek'))}
            aria-label="Επόμενη εβδομάδα"
            sx={{
              bgcolor: 'white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              '&:hover': { bgcolor: 'primary.main', color: 'white' },
              transition: 'all 0.2s',
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Paper>

      {/* Filters & Actions */}
      <Paper sx={{
        p: { xs: 1.5, md: 2 },
        mb: 3,
        borderRadius: 3,
        border: '1px solid rgba(0,0,0,0.06)',
        background: 'rgba(0,128,96,0.02)',
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: { xs: 'wrap', md: 'nowrap' },
        }}>
          <TextField
            size="small"
            label="Αναζήτηση ονόματος"
            placeholder="Όνομα..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: '100%', sm: 150, md: 165 },
              flexShrink: 0,
              '& .MuiOutlinedInput-root': { borderRadius: 2 },
              '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.75 },
              '& .MuiInputLabel-root': { fontSize: '0.8rem' },
            }}
          />
          <FormControl size="small" sx={{ width: { xs: '100%', sm: 130, md: 145 }, flexShrink: 0 }}>
            <InputLabel sx={{ fontSize: '0.8rem' }}>Φίλτρο Πόστων</InputLabel>
            <Select
              value={selectedPosition}
              label="Φίλτρο Πόστων"
              onChange={(e) => onPositionChange(e.target.value)}
              sx={{ borderRadius: 2, fontSize: '0.8rem', '& .MuiSelect-select': { py: 0.75 } }}
            >
              <MenuItem value=""><em>Όλα</em></MenuItem>
              {positions.map((position) => (
                <MenuItem key={position} value={position}>{position}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{
            display: 'flex',
            gap: 0.75,
            flexWrap: 'nowrap',
            flexShrink: 0,
            ml: { xs: 0, md: 'auto' },
            width: { xs: '100%', md: 'auto' },
            justifyContent: { xs: 'flex-start', md: 'flex-end' },
            overflowX: { xs: 'auto', md: 'visible' },
          }}>
            <Button
              variant={selectedForCopy.length > 0 ? 'contained' : 'outlined'}
              size="small"
              startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
              onClick={onOpenCopyWeek}
              disabled={shifts.length === 0}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.75rem',
                px: 1.25,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {selectedForCopy.length > 0 ? `Αντιγραφή (${selectedForCopy.length})` : 'Αντιγραφή Εβδομάδας'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="secondary"
              startIcon={generatingFixed ? null : <AutorenewIcon sx={{ fontSize: 16 }} />}
              onClick={onGenerateFixed}
              disabled={generatingFixed}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.75rem',
                px: 1.25,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {generatingFixed ? 'Δημιουργία...' : 'Σταθερά Προγράμματα'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="info"
              startIcon={<EmailIcon sx={{ fontSize: 16 }} />}
              onClick={onNotify}
              disabled={shifts.length === 0}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.75rem',
                px: 1.25,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              Ειδοποίηση Βαρδιών
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Schedule Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <Table sx={{ minWidth: 750 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ width: 40, padding: '8px' }}>
                <Checkbox
                  size="small"
                  sx={{ color: 'rgba(0,0,0,0.6)', '&.Mui-checked': { color: 'primary.main' } }}
                  checked={selectedForCopy.length > 0 && 
                    selectedForCopy.length === employees.filter(e => shifts.some(s => s.employee_id === e.id)).length}
                  indeterminate={selectedForCopy.length > 0 && 
                    selectedForCopy.length < employees.filter(e => shifts.some(s => s.employee_id === e.id)).length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onSelectedForCopyChange(employees.filter(emp => shifts.some(s => s.employee_id === emp.id)).map(emp => emp.id));
                    } else {
                      onSelectedForCopyChange([]);
                    }
                  }}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 120, color: '#333' }}>Υπάλληλος</TableCell>
              {DAYS.map((day) => (
                <TableCell key={day.value} align="center" sx={{ fontWeight: 600, minWidth: 60, color: '#333' }}>
                  {day.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {employees.map((employee) => {
              const hasShifts = shifts.some(s => s.employee_id === employee.id);
              const isSelected = selectedForCopy.includes(employee.id);
              
              return (
                <TableRow 
                  key={employee.id} 
                  hover
                  sx={{ backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'inherit' }}
                >
                  <TableCell sx={{ width: 40, padding: '8px' }}>
                    <Checkbox
                      size="small"
                      checked={isSelected}
                      disabled={!hasShifts}
                      onChange={(e) => {
                        if (e.target.checked) {
                          onSelectedForCopyChange([...selectedForCopy, employee.id]);
                        } else {
                          onSelectedForCopyChange(selectedForCopy.filter(id => id !== employee.id));
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>
                    <Box
                      onClick={() => onOpenEmployee(employee)}
                      sx={{
                        cursor: 'pointer',
                        color: 'primary.main',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      {employee.name}
                      {getEmployeeDisplayPosition && shifts.some((s) => s.employee_id === employee.id) && (
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                          {getEmployeeDisplayPosition(employee)}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  {DAYS.map((day) => {
                    const shift = getShiftForCell(employee.id, day.value);
                    const hasShift = !!shift;
                    const isMorning = shift?.shift_type === 'morning';
                    
                    return (
                      <TableCell
                        key={day.value}
                        align="center"
                        sx={{
                          padding: '8px 4px',
                          transition: 'background-color 0.2s ease',
                        }}
                      >
                        {hasShift ? (
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              gap: 0.5,
                              '&:hover .delete-btn': { opacity: 1 }
                            }}
                          >
                            <Chip
                              label={formatShiftDisplay(shift)}
                              onClick={() => onCellClick(employee, day.value)}
                              sx={{ 
                                cursor: 'pointer',
                                fontWeight: 600,
                                fontSize: '0.78rem',
                                height: 28,
                                minWidth: 80,
                                backgroundColor: isMorning ? '#c8e6c9' : '#2e7d32',
                                color: isMorning ? '#1b5e20' : '#fff',
                                border: 'none',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                                '&:hover': { 
                                  transform: 'scale(1.05)',
                                  boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
                                  backgroundColor: isMorning ? '#a5d6a7' : '#1b5e20'
                                }
                              }}
                            />
                            <IconButton
                              className="delete-btn"
                              size="small"
                              onClick={() => onDeleteShift(shift.id, employee.name)}
                              sx={{ 
                                padding: '2px', 
                                color: 'error.main',
                                opacity: 0,
                                transition: 'opacity 0.2s ease'
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box
                            onClick={() => onCellClick(employee, day.value)}
                            sx={{
                              cursor: 'pointer',
                              padding: '8px',
                              minHeight: 36,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 1,
                              border: '2px dashed transparent',
                              transition: 'all 0.2s ease',
                              '&:hover': { 
                                backgroundColor: 'rgba(102, 126, 234, 0.08)',
                                border: '2px dashed rgba(102, 126, 234, 0.3)'
                              }
                            }}
                          >
                            <AddIcon sx={{ fontSize: 18, color: '#bbb' }} />
                          </Box>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

